import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Trees, Users, Home, Activity, LogOut, UserCircle2, ShieldCheck, Crown, BookUser, Landmark } from "lucide-react";
import { ShareDialog } from "@/components/ShareDialog";

export const TopBar = () => {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const { pathname } = useLocation();

  const logged = !!user;
  const member = logged && (user.validated || user.is_super_admin || ["elder", "admin"].includes(user.role));
  const officer = logged && (user.is_super_admin || ["elder", "admin"].includes(user.role));
  const links = [
    { to: "/", label: t("village"), icon: Home, show: true },
    { to: "/clans", label: t("clans"), icon: Users, show: true },
    { to: "/histoire", label: "Histoire", icon: Landmark, show: true },
    { to: "/chefs", label: "Chefs", icon: Crown, show: true },
    { to: "/tree", label: t("tree"), icon: Trees, show: member },
    { to: "/registre", label: "Registre", icon: BookUser, show: member },
    { to: "/activity", label: t("activity"), icon: Activity, show: officer },
  ].filter(l => l.show);

  return (
    <header className="sticky top-0 z-40 bg-[#F7F3EE]/90 backdrop-blur-md border-b border-[#E3D5CA]" data-testid="top-bar">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <div className="w-9 h-9 rounded-full bg-[#993D22] flex items-center justify-center text-[#F7F3EE] font-serif-heritage text-lg font-semibold">E</div>
          <div className="hidden xl:block leading-tight">
            <div className="font-serif-heritage text-xl text-[#261911] font-semibold">Ekom 1</div>
            <div className="label-uppercase text-[#6E5B4F]">{t("tagline")}</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
          {links.map(({ to, label, icon: Icon }) => {
            const active = pathname === to || (to !== "/" && pathname.startsWith(to));
            return (
              <Link
                key={to}
                to={to}
                data-testid={`nav-${to.replace("/", "") || "home"}`}
                className={`flex items-center gap-1.5 px-2.5 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                  active ? "bg-[#993D22] text-[#F7F3EE]" : "text-[#3A2312] hover:bg-[#EDE3D7]"
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2 min-w-0">
          <ShareDialog />

          {user && user !== false ? (
            <>
              {(user.role === "admin" || user.is_super_admin) && (
                <Link
                  to="/admin"
                  data-testid="admin-link"
                  className="hidden sm:inline-flex items-center gap-1 px-3 py-2 rounded-full border border-[#993D22] text-sm text-[#993D22] hover:bg-[#993D22]/10"
                  title={t("adminPanel")}
                >
                  <ShieldCheck className="w-4 h-4" /> <span className="hidden lg:inline">{t("adminPanel")}</span>
                </Link>
              )}
              <Link to="/mon-compte" className="flex items-center gap-2 pl-2 sm:border-l border-[#E3D5CA] hover:text-[#993D22]" data-testid="user-badge" title="Mon compte">
                <UserCircle2 className="w-5 h-5 text-[#6B4226]" />
                <span className="hidden xl:inline text-sm text-[#3A2312] whitespace-nowrap">{user.name}</span>
                {user.role && user.role !== "contributor" && (
                  <span className="hidden sm:inline whitespace-nowrap label-uppercase text-[11px]" style={{ color: user.role === "admin" ? "#993D22" : "#2C4230" }}>
                    · {user.role === "admin" ? t("roleAdmin") : t("roleElder")}
                  </span>
                )}
              </Link>
              <Button onClick={() => { logout(); nav("/"); }} variant="ghost" size="sm" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : user === false ? (
            <>
              <Link to="/login" data-testid="login-link" className="btn-outline-terra text-sm whitespace-nowrap !px-3 sm:!px-5">{t("login")}</Link>
              <Link to="/register" data-testid="register-link" className="btn-terra text-sm whitespace-nowrap !px-3 sm:!px-5">{t("register")}</Link>
            </>
          ) : null}
        </div>
      </div>

      {/* Mobile nav */}
      <nav className="md:hidden flex items-center justify-around overflow-x-auto border-t border-[#E3D5CA] px-2 py-2 bg-[#F7F3EE]">
        {links.map(({ to, label, icon: Icon }) => {
          const active = pathname === to || (to !== "/" && pathname.startsWith(to));
          return (
            <Link
              key={to}
              to={to}
              data-testid={`mob-nav-${to.replace("/", "") || "home"}`}
              className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-md text-[11px] ${
                active ? "text-[#993D22] font-semibold" : "text-[#6E5B4F]"
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </Link>
          );
        })}
      </nav>
    </header>
  );
};

export default TopBar;
